import { motion, useInView, useMotionValue, useTransform, animate } from "framer-motion";
import { useEffect, useRef } from "react";

const stats = [
    { prefix: "", end: 50, suffix: "%", label: "fewer reception calls", sub: "answered by humans" },
    { prefix: ">", end: 95, suffix: "%", label: "call routing accuracy", sub: "across 1,700+ calls" },
    { static: "2–3×", label: "qualified leads", sub: "reaching attorneys" },
    { prefix: "", end: 90, suffix: "%", label: "less overflow", sub: "during intake rushes" },
];

const CountUp = ({ end, prefix = "", suffix = "", duration = 1.6 }) => {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: "-80px" });
    const mv = useMotionValue(0);
    const rounded = useTransform(mv, (latest) => `${prefix}${Math.round(latest)}${suffix}`);
    const textRef = useRef(null);

    useEffect(() => {
        if (!inView) return;
        const controls = animate(mv, end, { duration, ease: [0.16, 1, 0.3, 1] });
        const unsub = rounded.on("change", (v) => {
            if (textRef.current) textRef.current.textContent = v;
        });
        return () => { controls.stop(); unsub(); };
    }, [inView, end, duration, mv, rounded]);

    return (
        <span ref={ref} className="inline-block">
            <span ref={textRef}>{`${prefix}0${suffix}`}</span>
        </span>
    );
};

const ProofSection = () => {
    return (
        <section id="proof" data-testid="proof-section" className="relative py-24 md:py-32">
            <div className="max-w-[1280px] mx-auto px-6 md:px-10">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.7 }}
                    className="mb-14 md:mb-16 max-w-4xl"
                >
                    <span className="eyebrow-pill mb-5">Live deployment</span>
                    <h2 className="font-display text-[clamp(2rem,4.2vw,3.2rem)] leading-[1.04] tracking-tight font-semibold text-[var(--ink)] mt-5">
                        Real metrics from the firms{" "}
                        <span className="accent-blue">running Lexafide</span> today.
                    </h2>
                </motion.div>

                <div className="grid grid-cols-1 md:grid-cols-12 gap-5 md:gap-6 items-stretch">
                    {/* Quote card */}
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.7 }}
                        className="md:col-span-5 card-coral p-7 md:p-9 relative flex flex-col"
                    >
                        <i className="fa-solid fa-quote-left text-3xl text-[var(--coral)] mb-5" />
                        <p className="font-display font-medium text-[1.5rem] md:text-[1.7rem] leading-[1.2] text-[var(--ink)] mb-7">
                            &ldquo;Faster connections, better conversations. Lexafide routes every call and logs every detail, so our team just shows up and helps.&rdquo;
                        </p>
                        <div className="flex items-center gap-3 pt-5 mt-auto border-t border-[var(--coral)]/20">
                            <div className="w-10 h-10 rounded-full bg-[var(--coral)] flex items-center justify-center text-[var(--paper)] font-display font-semibold">L</div>
                            <div>
                                <div className="text-sm text-[var(--ink)]">Lizzy DePalma</div>
                                <div className="text-xs text-[var(--ink-dim)] font-mono">Richmond Vona · PI law firm</div>
                            </div>
                        </div>
                    </motion.div>

                    {/* Stats grid */}
                    <div className="md:col-span-7 grid grid-cols-2 gap-5 md:gap-6 auto-rows-fr">
                        {stats.map((s, i) => (
                            <motion.div
                                key={s.label}
                                initial={{ opacity: 0, y: 24 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.6, delay: 0.08 + i * 0.08 }}
                                whileHover={{ y: -4 }}
                                data-testid={`stat-card-${i}`}
                                className="card-paper p-5 sm:p-6 md:p-7 group flex flex-col min-h-[150px] sm:min-h-[170px]"
                            >
                                <div className="font-display text-[2.6rem] sm:text-5xl md:text-[3.4rem] lg:text-[3.6rem] text-[var(--ink)] mb-2 leading-none">
                                    {s.static ? s.static : <CountUp end={s.end} prefix={s.prefix} suffix={s.suffix} />}
                                </div>
                                <div className="mt-auto pt-3">
                                    <div className="text-[var(--ink)] text-[13px] sm:text-sm font-medium leading-snug" style={{ textWrap: "balance" }}>{s.label}</div>
                                    <div className="text-[var(--ink-dim)] text-[11px] sm:text-xs font-mono mt-1 leading-snug" style={{ textWrap: "balance" }}>{s.sub}</div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default ProofSection;
